import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CardComp, ButtonComp } from "../../../components";
import { formatMoney } from "../../../utils";
import toast from "react-hot-toast";
import {
  useVerifyPaymentStatusPublicQuery,
  useManuallyVerifyPaymentPublicMutation,
} from "../../../store/member/member-contribution";
import { investorButtonProps } from "../../../components/Investor/investorButtonStyles";
import { investmentHistoryPath } from "../../../utils/paymentConfirmationRoutes";

const AWAITING_MESSAGE =
  "We have not confirmed this payment yet. If you completed payment on ALATPay, it can take a few minutes. You can verify again, or check My requests.";

/**
 * Public return page for investment purchases (Paystack / ALATPay).
 * Verifies by payment reference only (no auth required).
 */
export default function InvestmentPaymentConfirmation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState("processing"); // processing | awaiting | success | failed
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const hasManuallyVerified = useRef(false);
  const hasLeftProcessing = useRef(false);
  const retryTimeoutRef = useRef(null);

  const MAX_RETRIES = 3;
  const RETRY_DELAY = 4000;

  const urlReference = searchParams.get("reference") || searchParams.get("trxref");
  const storedReference = sessionStorage.getItem("pending_payment_reference");
  const paymentReference = urlReference || storedReference;
  const storedRequestId = sessionStorage.getItem("pending_investment_request_id");

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (storedReference && !urlReference) {
      navigate(`/invest/payment/confirmation?reference=${storedReference}`, { replace: true });
    }
  }, [storedReference, urlReference, navigate]);

  useEffect(() => {
    if (urlReference && storedReference) {
      sessionStorage.removeItem("pending_payment_reference");
    }
  }, [urlReference, storedReference]);

  const {
    data: paymentData,
    error: paymentError,
    isLoading: isVerifyingPayment,
    refetch: refetchPayment,
  } = useVerifyPaymentStatusPublicQuery(
    { reference: paymentReference },
    { skip: !paymentReference, pollingInterval: 0 }
  );

  const [manuallyVerifyPayment, { isLoading: isManuallyVerifying }] =
    useManuallyVerifyPaymentPublicMutation();

  const markSuccess = useCallback((data) => {
    hasLeftProcessing.current = true;
    setPaymentStatus("success");
    setPaymentDetails(data);
    sessionStorage.removeItem("pending_payment_reference");
  }, []);

  const markFailed = useCallback((message) => {
    hasLeftProcessing.current = true;
    setPaymentStatus("failed");
    setPaymentDetails({ message });
  }, []);

  const enterAwaiting = useCallback((data = {}) => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    hasLeftProcessing.current = true;
    setPaymentStatus("awaiting");
    setPaymentDetails({
      ...data,
      payment_reference: data.payment_reference || paymentReference,
      total_amount: data.total_amount ?? data.amount,
      message: data.message || AWAITING_MESSAGE,
    });
  }, [paymentReference]);

  useEffect(() => {
    if (!paymentReference) {
      markFailed("No payment reference found. If you completed payment, check My requests.");
      return;
    }

    if (hasLeftProcessing.current && paymentStatus !== "processing") {
      return;
    }

    if (isVerifyingPayment) return;

    if (paymentError) {
      if (retryCount < MAX_RETRIES) {
        retryTimeoutRef.current = setTimeout(() => {
          setRetryCount((c) => c + 1);
          refetchPayment();
        }, RETRY_DELAY);
        return;
      }
      enterAwaiting({
        message: paymentError?.data?.message || AWAITING_MESSAGE,
      });
      return;
    }

    const status = String(paymentData?.status || "").toUpperCase();
    if (status === "SUCCESSFUL" || status === "SUCCESS") {
      markSuccess(paymentData);
      return;
    }

    if (status === "FAILED" || status === "CANCELLED") {
      markFailed(
        "Payment was not successful. Your unit hold may be released if you cancel the request."
      );
      return;
    }

    if (!hasManuallyVerified.current && retryCount === 0) {
      hasManuallyVerified.current = true;
      manuallyVerifyPayment({ reference: paymentReference })
        .unwrap()
        .then((data) => {
          const s = String(data?.status || data?.payment?.status || "").toUpperCase();
          if (s === "SUCCESSFUL" || s === "SUCCESS") {
            markSuccess(data?.payment || data);
          } else if (s === "FAILED" || s === "CANCELLED") {
            markFailed(data?.message || "Payment was not successful.");
          } else {
            setRetryCount(1);
            retryTimeoutRef.current = setTimeout(() => refetchPayment(), RETRY_DELAY);
          }
        })
        .catch(() => {
          setRetryCount(1);
          retryTimeoutRef.current = setTimeout(() => refetchPayment(), RETRY_DELAY);
        });
      return;
    }

    if (retryCount < MAX_RETRIES) {
      retryTimeoutRef.current = setTimeout(() => {
        setRetryCount((c) => c + 1);
        refetchPayment();
      }, RETRY_DELAY);
      return;
    }

    enterAwaiting(paymentData || {});
  }, [
    paymentReference,
    paymentData,
    paymentError,
    isVerifyingPayment,
    retryCount,
    refetchPayment,
    manuallyVerifyPayment,
    markSuccess,
    markFailed,
    enterAwaiting,
    paymentStatus,
  ]);

  const requestKey =
    paymentDetails?.request_reference ||
    sessionStorage.getItem("pending_investment_request_ref") ||
    paymentDetails?.investment_request_id ||
    storedRequestId ||
    null;

  const goToRequest = () => {
    sessionStorage.removeItem("pending_investment_request_id");
    sessionStorage.removeItem("pending_investment_request_ref");
    if (requestKey) {
      navigate(`/invest/requests/${encodeURIComponent(requestKey)}`);
      return;
    }
    navigate(investmentHistoryPath());
  };

  const handleVerifyAgain = async () => {
    if (!paymentReference) return;
    hasLeftProcessing.current = false;
    hasManuallyVerified.current = true;
    setPaymentStatus("processing");
    try {
      const data = await manuallyVerifyPayment({ reference: paymentReference }).unwrap();
      const s = String(data?.status || data?.payment?.status || "").toUpperCase();
      if (s === "SUCCESSFUL" || s === "SUCCESS") {
        markSuccess(data?.payment || data);
        return;
      }
      if (s === "FAILED" || s === "CANCELLED") {
        markFailed(data?.message || "Payment was not successful.");
        return;
      }
      enterAwaiting({
        ...(data?.payment || data || {}),
        message: data?.message || AWAITING_MESSAGE,
      });
    } catch (error) {
      enterAwaiting({
        message:
          error?.data?.message ||
          error?.message ||
          AWAITING_MESSAGE,
      });
    }
  };

  return (
    <div
      className="min-vh-100 d-flex align-items-center justify-content-center px-3"
      style={{
        background: "linear-gradient(160deg, #f7f7f7 0%, #eaf1f4 45%, #ffffff 100%)",
      }}
    >
      <CardComp cardClassName="border-0 shadow-sm rounded-4 p-4 p-md-5" style={{ maxWidth: 480, width: "100%" }}>
        {paymentStatus === "processing" && (
          <>
            <h4 className="fw-bold mb-2" style={{ color: "#383838" }}>
              Confirming your reservation…
            </h4>
            <p className="text-muted small mb-0">
              We are verifying your investment payment
              {isManuallyVerifying ? "" : ""}. This usually takes a few seconds.
            </p>
            <div className="text-center py-4">
              <div className="spinner-border" style={{ color: "var(--color-primary)" }} role="status">
                <span className="visually-hidden">Loading…</span>
              </div>
            </div>
          </>
        )}

        {paymentStatus === "awaiting" && (
          <>
            <div
              className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
              style={{
                width: 56,
                height: 56,
                backgroundColor: "rgba(255, 149, 0, 0.12)",
                color: "#ff9500",
                fontSize: 28,
                fontWeight: 700,
              }}
            >
              !
            </div>
            <h4 className="fw-bold mb-2" style={{ color: "#383838" }}>
              Payment not confirmed yet
            </h4>
            <p className="text-muted small mb-3">
              {paymentDetails?.message || AWAITING_MESSAGE}
            </p>
            <div className="bg-light rounded-3 p-3 mb-4 small">
              {paymentDetails?.payment_reference || paymentReference ? (
                <div className="d-flex justify-content-between mb-1 gap-2">
                  <span className="text-muted">Reference</span>
                  <span style={{ fontFamily: "monospace", wordBreak: "break-all", textAlign: "right" }}>
                    {paymentDetails?.payment_reference || paymentReference}
                  </span>
                </div>
              ) : null}
              {paymentDetails?.total_amount != null ? (
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Amount</span>
                  <strong>{formatMoney(paymentDetails.total_amount)}</strong>
                </div>
              ) : null}
            </div>
            <div className="d-flex flex-column gap-2">
              <ButtonComp
                btnText="Verify Payment"
                onClick={handleVerifyAgain}
                loading={isManuallyVerifying}
                {...investorButtonProps.primary}
                ButtonInlineStyle={{ ...investorButtonProps.primary.ButtonInlineStyle, width: "100%" }}
              />
              <ButtonComp
                btnText="Go to My Requests"
                onClick={goToRequest}
                {...investorButtonProps.outline}
                ButtonInlineStyle={{ ...investorButtonProps.outline.ButtonInlineStyle, width: "100%" }}
              />
              <ButtonComp
                btnText="Try investment again"
                onClick={() => {
                  sessionStorage.removeItem("pending_investment_request_id");
                  navigate("/invest/investments");
                }}
                {...investorButtonProps.outline}
                ButtonInlineStyle={{
                  ...investorButtonProps.outline.ButtonInlineStyle,
                  width: "100%",
                  borderColor: "#a1a0a0",
                  color: "#525252",
                }}
              />
            </div>
          </>
        )}

        {paymentStatus === "success" && (
          <>
            <h4 className="fw-bold mb-2" style={{ color: "#19bc31" }}>
              Reservation confirmed
            </h4>
            <p className="text-muted small mb-3">
              Your payment was successful and your units are reserved.
            </p>
            {paymentDetails?.total_amount != null && (
              <p className="mb-1 small">
                <span className="text-muted">Amount paid:</span>{" "}
                <strong>{formatMoney(paymentDetails.total_amount)}</strong>
              </p>
            )}
            {paymentDetails?.payment_reference && (
              <p className="mb-4 small text-muted">
                Ref: {paymentDetails.payment_reference}
              </p>
            )}
            <div className="d-flex flex-column gap-2">
              <ButtonComp
                btnText="View request"
                onClick={goToRequest}
                {...investorButtonProps.primary}
                ButtonInlineStyle={{ ...investorButtonProps.primary.ButtonInlineStyle, width: "100%" }}
              />
              <ButtonComp
                btnText="Browse opportunities"
                onClick={() => {
                  sessionStorage.removeItem("pending_investment_request_id");
                  navigate("/invest/investments");
                }}
                {...investorButtonProps.outline}
                ButtonInlineStyle={{ ...investorButtonProps.outline.ButtonInlineStyle, width: "100%" }}
              />
            </div>
          </>
        )}

        {paymentStatus === "failed" && (
          <>
            <h4 className="fw-bold mb-2" style={{ color: "#ff4343" }}>
              Payment not confirmed
            </h4>
            <p className="text-muted small mb-4">
              {paymentDetails?.message || "We could not confirm this payment."}
            </p>
            <div className="d-flex flex-column gap-2">
              <ButtonComp
                btnText="Verify Payment"
                onClick={handleVerifyAgain}
                loading={isManuallyVerifying}
                {...investorButtonProps.primary}
                ButtonInlineStyle={{ ...investorButtonProps.primary.ButtonInlineStyle, width: "100%" }}
              />
              <ButtonComp
                btnText="My requests"
                onClick={() => {
                  toast("You can cancel an unpaid request from My requests.");
                  goToRequest();
                }}
                {...investorButtonProps.outline}
                ButtonInlineStyle={{ ...investorButtonProps.outline.ButtonInlineStyle, width: "100%" }}
              />
            </div>
          </>
        )}
      </CardComp>
    </div>
  );
}
